/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";

import { UploadTabNames } from "./props";
import { DragDropUploadPDF } from "../../../../components/upload";
import DocumentView from "./DocumentView";
import api from "../../../../api";
import { useDataLoader } from "react-remote-data-hooks";
import {
  DraftProposalStatus,
  ListHeaderDropdown,
  MediaFile,
  ProposalReason,
} from "../../../../core";
import { toastr } from "react-redux-toastr";
import SendClientApprovalForm from "./SendClientApprovalForm";
import SignedTimesheet from "./SignedTimesheet";
import VersionList from "./VersionList";
import moment from "moment";
import { LeadActivities } from "../activities/lead-activities";
import { UserAvatar } from "../../../../components/render-file/UserAvatar";
import {
  Controller,
  useForm,
} from "../../../../../node_modules/react-hook-form/dist";
import { DropdownList } from "react-widgets";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import AddNewUserModal from "../../components/AddNewUser";
import SignedVersionList from "./SignedVersionList";
import { ContainerLoader } from "../../../../components/loader";
import { AddNewLandLord } from "../../../AddLandLord/AddNewLandLord";
import { FileFormats } from '../../../../utils/fileFormats';
import { isEqual, sortBy } from "lodash";
import AddUser from "../../../company/AddUser";

export interface FormProps {
  reason: string;
  comment?: string;
}

const UploadDocument: React.FunctionComponent<{
  onClose?: () => void;
  reload?: () => void;
  documentId: string;
  proposalDocuments?: any[];
  isSigned?: boolean;
  title?: string;
  leadId?: string;
  showAcceptButton?: boolean;
  showRejectButton?: boolean;
}> = (props) => {
  const [tabName, setTabName] = React.useState<UploadTabNames>(
    props.isSigned
      ? UploadTabNames.SignedTimesheet
      : UploadTabNames.TimesheetInProgess
  );
  const setElementActive = (tabValue: UploadTabNames) => {
    setTabName(tabValue);
  };
  const [isSaving, setSaving] = React.useState(false);

  const [documentId, setDocumentId] = useState(props.documentId);

  const [signedDocument, setSignedDocument] = useState();

  const { data: proposalDocument, reload, loading } = useDataLoader<any>(
    (docId: string) =>
      api.proposalDocument.findGenericProposalDocumentStageById(docId!),
    [documentId]
  );

  const {
    data: signedDocuments,
    reload: signedDocumentsReload,
    loading: signedDocumentsLoading,
  } = useDataLoader<any>(
    (docId: string) =>
      docId
        ? api.proposalDocument.getSignedDocumentsList(
            docId!,
            "Signed Client Document"
          )
        : undefined,
    [proposalDocument?.document?._id]
  );

  const [openNewUserModal, setOpenNewUserModal] = React.useState<boolean>(
    false
  );
  const [isReload, setIsReload] = React.useState<boolean>(false);
  const [companyId, setCompanyId] = React.useState<string>("");
  const [currentSelectedDocumentVersion, setCurrentSelectedDocumentVersion] = React.useState<string>(props.documentId)


  React.useEffect(() => {
    if (!loading) {
      reload(documentId);
    }
  }, [documentId]);

  React.useEffect(() => {
    if (!signedDocumentsLoading) {
      signedDocumentsReload(proposalDocument?.document?._id);
      setSignedDocument(proposalDocument?.signedClientDocument);
    }
  }, [proposalDocument?.document?._id]);

  const { control, errors, handleSubmit } = useForm<FormProps>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const { data: reasons } = useDataLoader<ProposalReason[]>(() =>
    api.proposal.loadAllProposalReason({ entityType: "Documents" })
  );

  const [statusModalOpen, setStatusModalOpen] = React.useState<{
    isOpen?: boolean;
    status?: DraftProposalStatus;
  }>({});

  const [showEdit, setShowEdit] = useState(false);

  const [openApprovalModal, setOpenApprovalModal] = React.useState<{
    isOpen?: boolean;
    approvalType?: string;
    user?: string[];
  }>({});

  const onFileChosen = async (file?: MediaFile) => {
    const createData = {
      proposedPropertyIds: proposalDocument?.proposedPropertyIds,
      documentSettingsId: proposalDocument?.documentSettingsId,
      stageCode: proposalDocument.stageCode,
      draftDocument: file,
    };

    const doc = await api.proposalDocument.createProposalDocumentStage(
      createData
    );
    reload(doc?._id);
    setShowEdit(false);
    props.reload?.();
    setCurrentSelectedDocumentVersion(doc?._id)
  };

  const onSend = async () => {
    setSaving(true);
    const createProposalData = {
      approvalType: "Internal",
      documentSettingsId: proposalDocument?.documentSettingsId,
      documentCopyType: "Client Copy",
    };

    await api.proposalDocument.requestApproval(
      createProposalData,
      proposalDocument?._id
    );
    setSaving(false);
    toastr.success(
      "Success",
      `${props.title} successfully sent for internal approval`
    );
    props.reload?.();
    reload(proposalDocument?._id);
  };
  const onApprove = async () => {
    setSaving(true);
    const createProposalData = {
      documentSettingsId: proposalDocument?.documentSettingsId,
      documentCopyType: "Client Copy",
    };

    await api.proposalDocument.internalApproval(
      createProposalData,
      proposalDocument?._id
    );
    setSaving(false);
    toastr.success("Success", `${props.title} successfully approved`);
    setStatusModalOpen({});
    props.reload?.();
    reload(proposalDocument?._id);
  };

  const onReject = async (data: any) => {
    setSaving(true);
    const createProposalData = {
      approvalType: "Internal",
      documentSettingsId: proposalDocument?.documentSettingsId,
      documentCopyType: "Client Copy",
      reason: data.reason,
      comment: data.comment,
    };

    await api.proposalDocument.rejectApproval(
      createProposalData,
      proposalDocument?._id
    );
    setSaving(false);
    toastr.success("Success", `${props.title} successfully rejected`);
    setStatusModalOpen({});
    props.reload?.();
    reload(proposalDocument?._id);
  };

  const actions: any[] = [];
  if (!proposalDocument?.document?.clientApproval) {
    actions.push({
      text: "Send For Client Approval",
      iconClass: "bg-orange",
      onClick: () => {
        setOpenApprovalModal({
          isOpen: true,
          approvalType: "Client",
        });
      },
    });
  }
  if (!proposalDocument?.document?.landlordApproval) {
    actions.push({
      text: "Send For Vendor / Owner Approval",
      iconClass: "bg-shamrock",
      onClick: () => {
        setOpenApprovalModal({
          isOpen: true,
          approvalType: "Landlord",
        });
      },
    });
  }

  const getStatus = () => {
    if (proposalDocument?.document?.internalApproval?.status === "Approved") {
      return "Internally Approved";
    }
    if (proposalDocument?.document?.internalApproval?.status === "Rejected") {
      return "Internally Rejected";
    }
    if (proposalDocument?.document?.internalApproval?.status === "Pending") {
      return "Internal Approval Pending";
    }
    return "Draft";
  };

  const renderApproveConfirm = () => {
    return statusModalOpen.isOpen &&
      statusModalOpen.status === DraftProposalStatus.Approved ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container py-4">
                  <div className="row justify-content-center">
                    <div className="col">
                      <p className="font-sm font-weight-bold mb-4">
                        Are you sure to Approve the {props.title}
                      </p>
                    </div>
                  </div>
                  <div className="row justify-content-center">
                    <div className="col-auto">
                      <button
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-md-4 px-4 mr-3"
                        onClick={() => {
                          setStatusModalOpen({});
                        }}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 flex-fill"
                        disabled={isSaving}
                        onClick={() => {
                          onApprove();
                        }}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  const renderRejectConfirm = () => {
    return statusModalOpen.isOpen &&
      statusModalOpen.status === DraftProposalStatus.Rejected ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <form onSubmit={handleSubmit(onReject)}>
                  <div className="container">
                    <div className="row">
                      <div className="col">
                        <p>Are you sure to Reject the {props.title}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <div className="form-group font-weight-semibold mb-3 text-left">
                          <label htmlFor="" className="font-sm text-uppercase">
                            Reason<span className="text-danger">*</span>
                          </label>
                          <Controller
                            control={control}
                            name="reason"
                            rules={{ required: true }}
                            render={({ onChange, value, name }) => (
                              <DropdownList
                                value={value}
                                data={reasons}
                                defaultValue={value}
                                textField={"reason"}
                                valueField={"reason"}
                                placeholder={"Select Reason"}
                                onChange={(val) => {
                                  onChange(val.reason);
                                }}
                              />
                            )}
                          />
                          {errors.reason && (
                            <ErrorMessage>Reason is required</ErrorMessage>
                          )}
                        </div>

                        <div className="form-group font-weight-semibold mb-3 text-left">
                          <label htmlFor="" className="font-sm text-uppercase">
                            Comments
                          </label>
                          <Controller
                            control={control}
                            name="comment"
                            render={({ onChange, value, name }) => (
                              <input
                                type="text"
                                className="form-control rounded-sm font-weight-semibold"
                                placeholder="Enter comment"
                                name={name}
                                value={value}
                                onChange={onChange}
                                id="comment"
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-auto mr-auto">
                        <button
                          className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-md-5 px-4 mr-3"
                          onClick={() => {
                            setStatusModalOpen({});
                          }}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="col-auto">
                        <button
                          className="btn btn-primary py-2 font-weight-semibold font-normal px-4 flex-fill"
                          disabled={isSaving}
                        >
                          Yes
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  const renderFile = () => {
    return (
      <div className="row gutter-10">
        <div className="col-sm-12 col-12 time-sheet-upload">
          <div className="form-group font-weight-semibold mb-0">
            <div className="drag-drop-file  position-relative">
              <DragDropUploadPDF
                name="timeSheet"
                id="timeSheet"
                onFileChosen={onFileChosen}
                title={`Upload ${props.title}`}
                accept={FileFormats}
              />
              {/* <p className="px-4 pt-4 text-danger font-xxs font-weight-normal text-gray-500">
                Note: Only PDF files are accepted.
              </p> */}

              <div className="mt-3 px-2">
                <div className="row">
                  <div className="align-items-center col-lg-auto col-md-2 d-flex font-xxs mb-3 mb-md-0">
                    <p className="m-0 pl-3">Download Template</p>
                  </div>
                  <div className="col-lg col-md-10">
                    {(props.title === "Term sheet" ||
                      props.title === "Term Sheet") && (
                      <ul className="list-unstyled mb-0">
                        <li className="text-primary font-xxs d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Term Sheet - Simple 2.doc"
                            download
                          >
                            <span className=" cursor-pointer">
                              Term Sheet - Simple 2.doc
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Term Sheet - Simple 1.doc"
                            download
                          >
                            <span className=" cursor-pointer">
                              Term Sheet - Simple 1.doc
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Term Sheet - Sale of Space.doc"
                            download
                          >
                            <span className=" cursor-pointer">
                              Term Sheet - Sale of Space.doc
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Term Sheet - Detailed.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Term Sheet - Detailed.docx
                            </span>
                          </a>
                        </li>
                      </ul>
                    )}
                    {props.title === "Letter of Intent" && (
                      <ul className="list-unstyled mb-0">
                        <li className="text-primary font-xxs d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Letter of Intent Format - Detailed 3.doc"
                            download
                          >
                            <span className=" cursor-pointer">
                              Letter of Intent Format - Detailed 3.doc
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Letter of Intent Format - Detailed 1.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Letter of Intent Format - Detailed 1.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Letter of Intent Format - Detailed 2.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Letter of Intent Format - Detailed 2.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Letter of Intent Format - Detailed - Sublease Space.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Letter of Intent Format - Detailed - Sublease
                              Space.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Letter of Intent Format - Simple.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Letter of Intent Format - Simple.docx
                            </span>
                          </a>
                        </li>
                      </ul>
                    )}
                     {props.title === "Agreement" && (
                      <ul className="list-unstyled mb-0">
                        <li className="text-primary font-xxs d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Draft Lease Agreement - Detailed - Format.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Draft Lease Agreement - Detailed - Format.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Draft Lease Agreement (Individual) Format.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Draft Lease Agreement (Individual) Format.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Draft Lease Agreement 1 (Partnership) Format.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Draft Lease Agreement 1 (Partnership) Format.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Draft Lease Agreement 2 (Partnership) Format.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Draft Lease Agreement 2 (Partnership) Format.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Draft Lease Agreement to Lease (Pvt Ltd) Format.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Draft Lease Agreement to Lease (Pvt Ltd) Format.docx
                            </span>
                          </a>
                        </li>
                        <li className="text-primary font-xxs  d-md-inline-block d-block bg-lightblue px-3 py-1 rounded-sm mr-1 mb-1">
                          <a
                            href="/downloads/samples/Draft Sub-Lease Agreement (Pvt Ltd) Format.docx"
                            download
                          >
                            <span className=" cursor-pointer">
                              Draft Sub-Lease Agreement (Pvt Ltd) Format.docx
                            </span>
                          </a>
                        </li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <div className="container-fluid border-bottom py-3 proposal-head">
      <div className="d-flex align-items-center">
        <>
          <div
            className="col-auto px-0 cursor-pointer"
            onClick={() => {
              if (showEdit) {
                setShowEdit(false);
              } else if (documentId !== props.documentId) {
                setDocumentId(props.documentId);
              } else {
                props.onClose?.();
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="26"
              height="26"
              viewBox="0 0 26 26"
              fill="none"
            >
              <path
                d="M25.0416 0.292893C24.6511 -0.0976308 24.0179 -0.0976312 23.6274 0.292893L12.6673 11.253L1.70711 0.292893C1.31658 -0.0976312 0.683417 -0.0976308 0.292893 0.292893C-0.0976308 0.683417 -0.0976312 1.31658 0.292893 1.70711L11.253 12.6673L0.292893 23.6274C-0.0976308 24.0179 -0.0976308 24.6511 0.292893 25.0416C0.683417 25.4322 1.31658 25.4322 1.70711 25.0416L12.6673 14.0815L23.6274 25.0416C24.0179 25.4322 24.6511 25.4322 25.0416 25.0416C25.4322 24.6511 25.4322 24.0179 25.0416 23.6274L14.0815 12.6673L25.0416 1.70711C25.4322 1.31658 25.4322 0.683417 25.0416 0.292893Z"
                fill="#6F7171"
              />
            </svg>
          </div>
          <div className="col">
            {proposalDocument?.proposedPropertyDetails.map((element: any,  index: number) => (
              <>
                <div className=" d-flex align-items-center">
                  <label className="font-md font-weight-semibold mb-0 pb-1">
                    {
                      element?.propertyDetails
                        ?.propertyDetailsFromProject?.projectName
                    }{" "}
                    / Floor :{" "}
                    {
                      element?.propertyDetails
                        ?.propertyDetailsFromProject?.floor
                    }{" "}
                    / Unit :{" "}
                    {
                      element?.propertyDetails
                        ?.propertyDetailsFromProject?.unitName
                    }{" "}
                    / Area :{" "}
                    {
                      element?.propertyDetails
                        ?.propertyDetailsFromProject?.area
                    }
                  </label>
                  <div className="d-flex pb-1">
                  {!!(proposalDocument.proposedPropertyDetails.length === index + 1) && <>
                  {!proposalDocument?.document?.clientApproval &&
                  !proposalDocument?.document?.landlordApproval ? (
                    <span
                      className={`bg-white font-sm px-2 py-1 requirement-title tag--pending border-primary border text-primary rounded-sm ml-3`}
                    >
                      {getStatus()}
                    </span>
                  ) : (
                    <>
                      {!!proposalDocument?.document?.clientApproval && (
                        <span
                          className={`bg-white font-sm px-2 py-1 requirement-title tag--pending border-primary border text-primary rounded-sm ml-3`}
                        >
                          {proposalDocument?.document?.clientApproval.status ===
                          "Pending"
                            ? "Sent to Client"
                            : "Client Approved"}
                        </span>
                      )}
                      {!!proposalDocument?.document?.landlordApproval && (
                        <span
                          className={`bg-white font-sm px-2 py-1 requirement-title tag--pending border-primary border text-primary rounded-sm ml-3`}
                        >
                          {proposalDocument?.document?.landlordApproval
                            .status === "Pending"
                            ? "Sent to Owner"
                            : "Vendor/Owner Approved"}
                        </span>
                      )}
                    </>
                  )}
                  </>}
                  </div>
                </div>
                {!!(proposalDocument.proposedPropertyDetails.length === index + 1) && <span className="d-block  font-xs text-gray-500 font-weight-semibold">
                  Created By{" "}
                  <span className="text-dark">
                    {proposalDocument?.createdBy?.name}
                  </span>{" "}
                  on{" "}
                  <span className="text-dark">
                    {moment(proposalDocument?.createdAt).format(
                      "DD MMM YYYY hh:mm A"
                    )}
                  </span>
                </span>}
              </>
            ))}
          </div>
          {!showEdit && (
            <div className="col-auto mt-md-0 mt-1 ml-auto">
              {proposalDocument?.document?.status === "Draft" &&
                !proposalDocument?.document?.internalApproval && (
                  <button
                    type="button"
                    onClick={onSend}
                    className="btn btn-primary px-4 font-normal font-weight-bold py-2 rounded"
                    disabled={isSaving}
                  >
                    Send For Internal Approval
                  </button>
                )}
              {proposalDocument?.document?.internalApproval?.status ===
                "Pending" && (
                <>
                  {props.showRejectButton && <button
                    type="button"
                    // onClick={onReject}
                    onClick={() => {
                      setStatusModalOpen({
                        isOpen: true,
                        status: DraftProposalStatus.Rejected,
                      });
                    }}
                    className="btn btn-outline-primary bg-white text-primary px-4 font-normal font-weight-bold py-2 rounded mx-2 "
                    disabled={isSaving}
                  >
                    Reject
                  </button>}
                  {props.showAcceptButton && <button
                    type="button"
                    // onClick={onApprove}
                    onClick={() => {
                      setStatusModalOpen({
                        isOpen: true,
                        status: DraftProposalStatus.Approved,
                      });
                    }}
                    className="btn btn-primary px-4 font-normal font-weight-bold py-2 rounded"
                    disabled={isSaving}
                  >
                    Approve
                  </button>}
                </>
              )}
            </div>
          )}
          {!!actions?.length &&
            !showEdit &&
            proposalDocument?.document?.internalApproval?.status ===
              "Approved" && (
              <div className="mt-md-0 ml-auto col-auto">
                <ListHeaderDropdown text="Action" actions={actions} />
              </div>
            )}
        </>
      </div>
    </div>
  );

  const renderAddNewUserHeader = () => {
    return (
      <div className="container-fluid bg-gray-100 border-bottom py-3 position-sticky top-0 zIndex-9">
        <div className="row align-items-center">
          <div className="col-auto" onClick={() => setOpenNewUserModal(false)}>
            <i className="icon-close text-gray-500 font-xxl cursor-pointer"></i>
          </div>
          <div className="col-auto font-base font-weight-bold">
            Add New User
          </div>
        </div>
      </div>
    );
  };

  const NewUserModal = (propertyId: string, approvalType: string) => {
    return (
      <div
      className={`detail-poupup position-fixed top-0 h-100 overflow-auto in ${approvalType === "Client"? "w-100 ml-2":"w-50"}`}
      style={{ maxWidth: approvalType === "Client" ? "100%":"600px", zIndex: 1055 }}
      >
        {renderAddNewUserHeader()}
        {approvalType === "Client" ? (
          <>
          {/* <AddNewUserModal
            onClose={() => setOpenNewUserModal(false)}
            onCreateUser={() => setIsReload(true)}
            approvalType={approvalType}
            propertyId={propertyId || ""}
            companyId={companyId}
            gridColumns={3}
            /> */}
             <AddUser
                onClose={() => setOpenNewUserModal(false)}
                customerCreated={()=>setIsReload(true)}
                companyId={companyId}
            />
            </>
        ) : (
          <AddNewLandLord
            onClose={() => setOpenNewUserModal(false)}
            onCreateUser={() => setIsReload(true)}
            propertyId={propertyId || ""}
            approvalType={approvalType}
            companyId={companyId}
            AddNewUser={AddNewUserModal}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={`bg-gray-100 detail-poupup  position-fixed top-0 w-100 h-100  in time-sheet-popup`}
        style={{ zIndex: 1052 }}
      >
        {renderHeader()}
        {loading && !proposalDocument ? (
          <ContainerLoader height={200}></ContainerLoader>
        ) : (
          <>
            {!showEdit ? (
              <div className="container-fluid px-0 h-100">
                <div className="">
                  <ul
                    className="d-flex list-unstyled tab-underline font-sm font-weight-semibold border-bottom text-nowrap overflow-auto"
                    id="activity-tab"
                    role="tablist"
                  >
                    {!props.isSigned && (
                      <li className="nav-item pr-4 pl-4" role="presentation">
                        <a
                          className={`cursor-pointer nav-link position-relative px-0 py-2 text-gray-600 ${
                            tabName === UploadTabNames.TimesheetInProgess
                              ? "active"
                              : ""
                          }`}
                          onClick={(e) => {
                            setOpenApprovalModal({});
                            setElementActive(UploadTabNames.TimesheetInProgess);
                          }}
                        >
                          <span className="py-1 d-inline-flex">
                            {props.title} In Progess
                          </span>
                        </a>
                      </li>
                    )}

                    <li className="nav-item pr-4 pl-4" role="presentation">
                      <a
                        className={`cursor-pointer nav-link position-relative px-0 py-2 text-gray-600 ${
                          tabName === UploadTabNames.TimesheetVersion
                            ? "active"
                            : ""
                        }`}
                        onClick={(e) => {
                          // if (!!allFiles?.length) {
                          setElementActive(UploadTabNames.TimesheetVersion);
                          setOpenApprovalModal({});
                          // }
                        }}
                      >
                        <span className="py-1 d-inline-flex">
                          {props.title} Version
                        </span>
                      </a>
                    </li>

                    {!!proposalDocument?.document?.clientApproval &&
                      !!proposalDocument?.document?.landlordApproval && (
                        <>
                          <li
                            className="nav-item pr-4 pl-4"
                            role="presentation"
                          >
                            <a
                              className={`cursor-pointer nav-link position-relative px-0 py-2 text-gray-600 ${
                                tabName === UploadTabNames.SignedTimesheet
                                  ? "active"
                                  : ""
                              }`}
                              onClick={(e) => {
                                setElementActive(
                                  UploadTabNames.SignedTimesheet
                                );
                                setOpenApprovalModal({});
                              }}
                            >
                              <span className="py-1 d-inline-flex">
                                Signed {props.title}
                              </span>
                            </a>
                          </li>
                          <li
                            className="nav-item pr-4 pl-4"
                            role="presentation"
                          >
                            <a
                              className={`cursor-pointer nav-link position-relative px-0 py-2 text-gray-600 ${
                                tabName ===
                                UploadTabNames.SignedTimesheetVersion
                                  ? "active"
                                  : ""
                              }`}
                              onClick={(e) => {
                                // if (!!allFiles?.length) {
                                setElementActive(
                                  UploadTabNames.SignedTimesheetVersion
                                );
                                setOpenApprovalModal({});
                                // }
                              }}
                            >
                              <span className="py-1 d-inline-flex">
                                Signed {props.title} Version
                              </span>
                            </a>
                          </li>
                        </>
                      )}
                  </ul>
                </div>
                <div className="h-100 mt-2 px-4 row">
                  <div
                    className={`${
                      tabName === UploadTabNames.TimesheetVersion ||
                      tabName === UploadTabNames.SignedTimesheetVersion
                        ? "col-12"
                        : "col-md-8 col-12"
                    }`}
                  >
                    {tabName === UploadTabNames.TimesheetInProgess && ( 
                      <>
                        <DocumentView
                          currentFile={proposalDocument}
                          reload={() => {
                            props.reload?.();
                            reload(proposalDocument?._id);
                          }}
                          onEdit={() => setShowEdit(true)}
                          versions={props.proposalDocuments?.filter(
                            (x) =>
                                Array.isArray(proposalDocument?.proposedPropertyIds) &&
                                isEqual(sortBy(proposalDocument?.proposedPropertyIds), sortBy(x.proposedPropertyIds)) &&
                                x.stageCode === proposalDocument?.stageCode
                        )}
                          changeDocument={(id: string) => {
                            reload(id);
                            setCurrentSelectedDocumentVersion(id)
                          }}
                          title={props.title}
                        ></DocumentView>
                      </>
                    )}

                    {tabName === UploadTabNames.SignedTimesheet && (
                      <>
                        <SignedTimesheet
                          versions={props.proposalDocuments?.filter(
                            (x) =>
                              Array.isArray(proposalDocument?.proposedPropertyIds) &&
                                isEqual(sortBy(proposalDocument?.proposedPropertyIds), sortBy(x.proposedPropertyIds)) &&
                                x.stageCode === proposalDocument?.stageCode &&
                              x.document?.internalApproval?.status ===
                                "Approved" &&
                              !!x?.document?.clientApproval &&
                              !!x?.document?.landlordApproval
                          )}
                          proposalId={
                            proposalDocument?.proposedPropertyDetails[0]
                              ?.proposalId
                          }
                          documentSettingsId={
                            proposalDocument?.documentSettingsId
                          }
                          documentId={proposalDocument?._id}
                          onSuccess={(doc: any) => {
                            props.reload?.();
                            reload(proposalDocument?._id);
                            setSignedDocument(doc);
                            signedDocumentsReload(
                              proposalDocument?.document?._id
                            );
                          }}
                          title={props.title}
                          signedDocument={signedDocument}
                          proposedPropertyId={
                            proposalDocument?.proposedPropertyDetails[0]
                              ?.propertyDetails?.propertyId
                          }
                        ></SignedTimesheet>
                      </>
                    )}

                    {tabName === UploadTabNames.TimesheetVersion && (
                      <>
                        <VersionList
                          versions={props.proposalDocuments?.filter(
                            (x) =>
                              Array.isArray(proposalDocument?.proposedPropertyIds) &&
                            isEqual(sortBy(proposalDocument?.proposedPropertyIds), sortBy(x.proposedPropertyIds)) &&
                            x.stageCode === proposalDocument?.stageCode
                          )}
                          onView={(docId: string) => {
                            setDocumentId(docId);
                            setElementActive(UploadTabNames.TimesheetInProgess);
                          }}
                        ></VersionList>
                      </>
                    )}
                    {tabName === UploadTabNames.SignedTimesheetVersion && (
                      <>
                        <SignedVersionList
                          showAgreementExpiryDate
                          versions={signedDocuments}
                          onView={(doc: any) => {
                            setSignedDocument(doc);
                            setElementActive(UploadTabNames.SignedTimesheet);
                          }}
                          title={props.title}
                        ></SignedVersionList>
                      </>
                    )}
                  </div>
                  {tabName !== UploadTabNames.TimesheetVersion &&
                    tabName !== UploadTabNames.SignedTimesheetVersion && (
                      <div className="col-md-4 col-12 ">
                        {(proposalDocument?.signedClientDocument
                          ?.clientSignedBy ||
                          proposalDocument?.signedLandlordDocument
                            ?.landLordSignedBy) && (
                          <ul
                            className="list-unstyled bg-white border d-flex"
                            style={{ borderRadius: "4px" }}
                          >
                            {proposalDocument?.signedClientDocument
                              ?.clientSignedBy && (
                              <li className="border-right w-50 py-3 px-3 d-flex flex-wrap align-items-center">
                                <h6 className="font-sm font-weight-semibold text-gray-500">
                                  Client Acceptance & Agreed By
                                </h6>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="figure"
                                    style={{ width: "26px", height: "26px" }}
                                  >
                                    <UserAvatar
                                      id={
                                        proposalDocument?.signedClientDocument
                                          ?.clientSignedBy?._id
                                      }
                                      className="img-fluid img-cover"
                                      alt=""
                                      title={
                                        proposalDocument?.signedClientDocument
                                          ?.clientSignedBy?.fullName
                                      }
                                      imageUrl={
                                        proposalDocument?.signedClientDocument
                                          ?.clientSignedBy?.avatar?.location
                                      }
                                      image={
                                        proposalDocument?.signedClientDocument
                                          ?.clientSignedBy?.avatar
                                      }
                                    ></UserAvatar>
                                  </div>
                                  <div className="fig-caption font-sm font-weight-semibold pl-2">
                                    {
                                      proposalDocument?.signedClientDocument
                                        ?.clientSignedBy?.fullName
                                    }{" "}
                                    <span className="d-block text-gray-500">
                                      on{" "}
                                      {moment(
                                        proposalDocument?.signedClientDocument
                                          ?.clientSignedAt
                                      ).format("DD/MMM/YYYY")}{" "}
                                    </span>
                                  </div>
                                </div>
                              </li>
                            )}

                            {proposalDocument?.signedLandlordDocument
                              ?.landLordSignedBy && (
                              <li className="w-50 py-3 px-3 d-flex flex-wrap align-items-center">
                                <h6 className="font-sm font-weight-semibold text-gray-500">
                                  Vendor/ Owner Acceptance & Agreed By
                                </h6>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="figure"
                                    style={{ width: "26px", height: "26px" }}
                                  >
                                    <UserAvatar
                                      id={
                                        proposalDocument?.signedLandlordDocument
                                          ?.landLordSignedBy?._id
                                      }
                                      className="img-fluid img-cover"
                                      alt=""
                                      title={
                                        proposalDocument?.signedLandlordDocument
                                          ?.landLordSignedBy?.fullName
                                      }
                                      imageUrl={
                                        proposalDocument?.signedLandlordDocument
                                          ?.landLordSignedBy?.avatar?.location
                                      }
                                      image={
                                        proposalDocument?.signedLandlordDocument
                                          ?.landLordSignedBy?.avatar
                                      }
                                    ></UserAvatar>
                                  </div>
                                  <div className="fig-caption font-sm font-weight-semibold pl-2">
                                    {
                                      proposalDocument?.signedLandlordDocument
                                        ?.landLordSignedBy?.fullName
                                    }{" "}
                                    <span className="d-block text-gray-500">
                                      on{" "}
                                      {moment(
                                        proposalDocument?.signedLandlordDocument
                                          ?.landLordSignedAt
                                      ).format("DD/MMM/YYYY")}{" "}
                                    </span>
                                  </div>
                                </div>
                              </li>
                            )}
                          </ul>
                        )}

                        {documentId && props.leadId && (
                          <LeadActivities
                            leadId={props.leadId!}
                            documentId={documentId!}
                            stageCode={proposalDocument?.stageCode}
                            genericProposalDocumentId={
                              props.proposalDocuments?.filter(
                                (x) =>
                                  currentSelectedDocumentVersion === x._id && x.stageCode === proposalDocument?.stageCode
                              )?.[0]?._id
                            }
                            onUpdate={() => {
                              // loadAllLog({
                              //   page: 1,
                              //   leadId: leadState?.leadDetails?._id,
                              //   customerId: leadState?.leadDetails?.customer?._id,
                              //   includeAllCustomerLogs: true,
                              // });
                              // closeAccordion();
                            }}
                            // onReload={props.onReload}
                          ></LeadActivities>
                        )}
                      </div>
                    )}
                </div>
              </div>
            ) : (
              <div className="row m-0">
                <div className="col-8 bg-white pt-3  mx-4 my-2 border pb-4 pl-0 pr-0">
                  <div className="d-flex align-items-center mb-3 px-4">
                    <label className="font-md font-weight-semibold mb-0 ">
                      {props.title || "Term Sheet"}
                    </label>
                  </div>
                  <div className="w-100">
                    <div>{renderFile()}</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {renderApproveConfirm()}
        {renderRejectConfirm()}
      </div>

      <div className="modal-backdrop fade show" style={{ zIndex: 1051 }}></div>
      {openApprovalModal.isOpen && (
        <SendClientApprovalForm
          onClose={() => setOpenApprovalModal({})}
          approvalType={openApprovalModal.approvalType}
          proposalId={proposalDocument?.proposedPropertyDetails[0]?.proposalId}
          documentSettingsId={proposalDocument?.documentSettingsId}
          onSuccess={() => {
            setOpenApprovalModal({});
            props.reload?.();
            reload(proposalDocument?._id);
          }}
          documentId={proposalDocument?._id}
          title={props.title}
          documentCopyType={"Client Copy"}
          onAddUser={(compId) => {
            setCompanyId(compId);
            setOpenNewUserModal(true);
            setIsReload(false);
          }}
          proposedPropertyId={
              proposalDocument?.proposedPropertyDetails[0]?.propertyDetails
              ?.propertyId
          }
          isReloadUser={isReload}
          companyDetails={proposalDocument?.companyDetails}
        ></SendClientApprovalForm>
      )}

      {openNewUserModal &&
        NewUserModal(
          proposalDocument?.proposedPropertyDetails[0]?.propertyDetails
            ?.propertyId,
          openApprovalModal?.approvalType || ""
        )}
    </>
  );
};

export default UploadDocument;
